import React, { FC, useCallback, useState } from 'react';

import dlv from 'dlv';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { logExternalOfferClick, logOfferClick } from 'components/offers/custom-events';
import { IExternalOffers } from 'generated/sanity-graphql';
import { useLoyaltyExternalOffers } from 'hooks/use-loyalty-external-offers';
import useMediaQuery from 'hooks/use-media-query';
import { AvailableTabsType } from 'pages/loyalty/loyalty-offers/types';
import { useCdpContext } from 'state/cdp';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { LoyaltyOffer } from 'state/loyalty/types';
import { isTestOnly } from 'state/loyalty/utils';
import { routes } from 'utils/routing';

import OfferCarouselItem from './offer-carousel-item';
import {
  CarouselCardContainer,
  CarouselContainer,
  CarouselHeader,
  CarouselLink,
  CarouselNextButton,
  CarouselPrevButton,
  CarouselScrollableWrapper,
  CarouselTitle,
  CarouselWrapper,
} from './offer-carousel-section.styled';
import { CarouselDirection, IOfferCarouselSectionProps } from './types';

const OfferCarouselSection: FC<IOfferCarouselSectionProps> = ({ title, offers }) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const isMobile = useMediaQuery('mobile');
  const cdp = useCdpContext();

  const [step, setStep] = useState(0);
  const numOffers = Number(offers?.length);
  const maxStep = numOffers > 6 ? Math.ceil((numOffers - 6) / 2) : 0;

  // If less than 2 offers are added to the carousel we display them without any scrolling
  const shouldScroll = Boolean(numOffers > 2);

  const hasExternalOffers = offers?.some(offer => offer?._type === 'externalOffers');
  const { externalOffersLoading } = useLoyaltyExternalOffers(!hasExternalOffers);

  const systemOfferClick = useCallback(
    (offer: LoyaltyOffer, index: number) => {
      logOfferClick(offer, index, numOffers, cdp);
      navigate(`${routes.rewardsOffers}/${offer.loyaltyEngineId}`, {
        state: {
          backRoute: pathname,
        },
      });
    },
    [cdp, navigate, numOffers, pathname]
  );

  const externalOfferClick = useCallback(
    (offer: IExternalOffers, index: number) => {
      logExternalOfferClick(offer, index, numOffers, cdp);
      navigate(`${routes.rewardsOffers}/${offer._id}`, {
        state: {
          backRoute: pathname,
          selectedTab: AvailableTabsType.ExternalOffers,
        },
      });
    },
    [navigate, pathname, cdp, numOffers]
  );

  const handleSeeAll = useCallback(() => {
    navigate(routes.rewardsOffers);
  }, [navigate]);

  const shiftOffers = useCallback(
    (direction: number) => {
      setStep(step + direction);
    },
    [step]
  );

  if (!loyaltyEnabled || !offers?.length) {
    return null;
  }

  return (
    <CarouselContainer>
      <CarouselHeader>
        <CarouselTitle>{title?.locale}</CarouselTitle>
        <CarouselLink onClick={handleSeeAll}>
          {formatMessage({ id: 'loyaltyOffersSeeAll' })}
        </CarouselLink>
      </CarouselHeader>
      <CarouselWrapper>
        <CarouselScrollableWrapper>
          {!isMobile && step ? (
            <CarouselPrevButton onClick={() => shiftOffers(CarouselDirection.ShiftLeft)} />
          ) : null}
          <CarouselCardContainer
            offset={step}
            shouldScroll={isMobile ? shouldScroll : offers?.length > 3}
          >
            {offers.map((offer, index) => {
              if (isTestOnly(offer)) {
                return null;
              }

              switch (offer?._type) {
                case 'systemwideOffer':
                  const systemOffer = offer as LoyaltyOffer;
                  return (
                    <OfferCarouselItem
                      offerId={systemOffer.loyaltyEngineId}
                      favoriteId={systemOffer._id}
                      offerName={dlv(systemOffer, 'name.localeRaw.0.children.0.text', '')}
                      description={dlv(systemOffer, 'description.localeRaw.0.children.0.text', '')}
                      image={dlv(systemOffer, 'localizedImage.locale.app')}
                      imageAlt={dlv(systemOffer, 'localizedImage.locale.app.imageDescription')}
                      fullWidth={!shouldScroll}
                      onClick={() => systemOfferClick(systemOffer, index)}
                    />
                  );
                case 'externalOffers':
                  const externalOffer = offer as IExternalOffers;
                  if (externalOffersLoading) {
                    return null;
                  }
                  return (
                    <OfferCarouselItem
                      offerId={externalOffer._id}
                      favoriteId={externalOffer._id}
                      offerName={dlv(externalOffer, 'title.locale', '')}
                      description={dlv(
                        externalOffer,
                        'description.localeRaw.0.children.0.text',
                        ''
                      )}
                      image={dlv(externalOffer, 'image.locale')}
                      imageAlt={dlv(externalOffer, 'image.locale.imageDescription')}
                      fullWidth={!shouldScroll}
                      onClick={() => externalOfferClick(externalOffer, index)}
                    />
                  );
                default:
                  return null;
              }
            })}
          </CarouselCardContainer>
          {!isMobile && step !== maxStep ? (
            <CarouselNextButton onClick={() => shiftOffers(CarouselDirection.ShiftRight)} />
          ) : null}
        </CarouselScrollableWrapper>
      </CarouselWrapper>
    </CarouselContainer>
  );
};

export default OfferCarouselSection;
